import { useEffect, useState } from 'react';
import { collection, onSnapshot, deleteDoc, doc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link } from 'react-router-dom';

const sidebarLinks = [
  { to: '/', label: '🏠 Dashboard' },
  { to: '/matches', label: '🎯 Matches' },
  { to: '/results', label: '🏆 Results' },
  { to: '/users', label: '👥 Users' },
  { to: '/deposits', label: '💰 Deposits' },
  { to: '/withdrawals', label: '⬆️ Withdrawals' },
  { to: '/settings', label: '⚙️ Settings' },
  { to: '/password-resets', label: '🔑 Password Resets', active: true },
  { to: '/videos', label: '🎬 Videos' }
];

export default function PasswordResets() {
  const [requests, setRequests] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'passwordResetRequests'), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      setRequests(data);
    });
    return () => unsub();
  }, []);

  // ── Password Change ──────────────────────────────────────────────────────
  const handleChangePassword = async (r) => {
    if (!window.confirm(`📱 0${r.phone} নম্বরের user এর password change করবে?`)) return;

    setLoadingId(r.id);
    try {
      // phone দিয়ে Firestore থেকে UID নাও
      const q = query(collection(db, 'users'), where('phone', '==', r.phone));
      const snap = await getDocs(q);

      if (snap.empty) {
        alert('❌ User পাওয়া যায়নি! Firestore এ users collection চেক করো।');
        setLoadingId(null);
        return;
      }

      const uid = snap.docs[0].id;

      // Backend এ call করো
      const response = await fetch('https://ffarena-backend-production.up.railway.app/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, newPassword: r.newPassword }),
      });

      const data = await response.json();

      if (data.success) {
        // Request delete করো
        await deleteDoc(doc(db, 'passwordResetRequests', r.id));
        alert('✅ Password change হয়েছে!\nUser এখন নতুন password দিয়ে login করতে পারবে।');
      } else {
        alert('❌ সমস্যা: ' + data.error);
      }
    } catch (err) {
      if (err.message.includes('fetch')) {
        alert('❌ Backend চালু নেই!\n\nVS Code Terminal এ এই command দাও:\nnode index.js');
      } else {
        alert('❌ Error: ' + err.message);
      }
    }
    setLoadingId(null);
  };

  // ── Delete / Done ────────────────────────────────────────────────────────
  const handleDone = async (id) => {
    if (!window.confirm('এই request টা delete করবে?')) return;
    await deleteDoc(doc(db, 'passwordResetRequests', id));
  };

  return (
    <div style={styles.container}>

      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarLogo}>🎮 FF Arena</div>
        <div style={styles.sidebarSub}>Admin Panel</div>
        <nav style={styles.nav}>
          {sidebarLinks.map(l => (
            <Link key={l.to} to={l.to}
              style={l.active ? styles.navItemActive : styles.navItem}>
              {l.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main */}
      <div style={styles.main}>
        <h1 style={styles.pageTitle}>🔑 Password Reset Requests</h1>
        <p style={styles.pageSub}>
          User দের password reset request — নিচে "Change Password" চাপলেই Firebase এ automatically update হবে
        </p>

        {/* How to guide */}
        <div style={styles.guideCard}>
          <div style={styles.guideTitle}>📌 কীভাবে Password Reset করবে:</div>
          <div style={styles.guideText}>
            1. Backend চালু রাখো — VS Code Terminal এ <code style={styles.code}>node index.js</code> দাও<br />
            2. নিচে user এর phone number, email ও নতুন password দেখো<br />
            3. <b style={{ color: '#a78bfa' }}>"🔑 Change Password"</b> বাটনে চাপো — সাথে সাথে Firebase এ update হবে<br />
            4. Done হলে request automatically delete হয়ে যাবে ✅
          </div>
        </div>

        {/* Backend status reminder */}
        <div style={styles.alertCard}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span style={{ color: '#f4c430', fontSize: 13 }}>
            <b>Backend চালু আছে কি?</b> VS Code Terminal এ <code style={styles.code}>node index.js</code> দিয়ে রাখো, নাহলে password change হবে না।
          </span>
        </div>

        {/* Stats */}
        <div style={styles.statCard}>
          <div style={{ color: '#f4c430', fontSize: 32, fontWeight: 'bold' }}>
            {requests.filter(r => r.status === 'pending').length}
          </div>
          <div style={{ color: '#666', fontSize: 13 }}>Pending Requests</div>
        </div>

        {/* Table */}
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Phone Number', 'Email', 'New Password', 'Request Time', 'Status', 'Action'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#555', padding: 40 }}>
                    কোনো request নেই ✅
                  </td>
                </tr>
              ) : requests.map(r => (
                <tr key={r.id}>
                  {/* Phone */}
                  <td style={styles.td}>
                    <span style={{ color: '#f4c430', fontSize: 15, fontWeight: 'bold' }}>
                      📱 0{r.phone}
                    </span>
                  </td>

                  {/* Email */}
                  <td style={styles.td}>
                    <span style={{ color: '#888', fontSize: 12 }}>
                      {r.email ?? '—'}
                    </span>
                  </td>

                  {/* New Password */}
                  <td style={styles.td}>
                    <span style={styles.passwordBadge}>
                      🔒 {r.newPassword ?? '—'}
                    </span>
                  </td>

                  {/* Request Time */}
                  <td style={styles.td}>
                    <span style={{ color: '#666', fontSize: 12 }}>
                      {r.createdAt?.seconds
                        ? new Date(r.createdAt.seconds * 1000).toLocaleString()
                        : '—'}
                    </span>
                  </td>

                  {/* Status */}
                  <td style={styles.td}>
                    <span style={styles.pendingBadge}>⏳ Pending</span>
                  </td>

                  {/* Action */}
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        style={{
                          ...styles.changeBtn,
                          opacity: loadingId === r.id ? 0.6 : 1,
                          cursor: loadingId === r.id ? 'not-allowed' : 'pointer',
                        }}
                        onClick={() => handleChangePassword(r)}
                        disabled={loadingId === r.id}
                      >
                        {loadingId === r.id ? '⏳ হচ্ছে...' : '🔑 Change Password'}
                      </button>
                      <button
                        style={styles.doneBtn}
                        onClick={() => handleDone(r.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', minHeight: '100vh', background: '#0a0a1a' },
  sidebar: {
    width: 240, background: '#111128', borderRight: '1px solid #1e1e3a',
    padding: '24px 0', display: 'flex', flexDirection: 'column',
    position: 'fixed', height: '100vh',
  },
  sidebarLogo: { color: '#f4c430', fontSize: 20, fontWeight: 'bold', padding: '0 24px', marginBottom: 4 },
  sidebarSub: { color: '#555', fontSize: 12, padding: '0 24px', marginBottom: 32 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: {
    color: '#888', textDecoration: 'none', padding: '12px 24px',
    fontSize: 14, fontWeight: '500', borderLeft: '3px solid transparent',
  },
  navItemActive: {
    color: '#f4c430', textDecoration: 'none', padding: '12px 24px',
    fontSize: 14, fontWeight: '600', borderLeft: '3px solid #f4c430',
    background: '#f4c43011',
  },
  main: { marginLeft: 240, flex: 1, padding: 32 },
  pageTitle: { color: '#fff', fontSize: 28, fontWeight: 'bold', margin: 0 },
  pageSub: { color: '#666', fontSize: 14, marginTop: 6, marginBottom: 24 },

  guideCard: {
    background: '#111128', borderRadius: 14,
    border: '1px solid #f4c43033', padding: 20, marginBottom: 16,
  },
  guideTitle: { color: '#f4c430', fontSize: 14, fontWeight: 'bold', marginBottom: 10 },
  guideText: { color: '#888', fontSize: 13, lineHeight: 2.2 },
  code: {
    background: '#1e1e3a', color: '#00c896',
    padding: '2px 8px', borderRadius: 6, fontSize: 12,
    fontFamily: 'monospace',
  },

  alertCard: {
    background: '#f4c43011', border: '1px solid #f4c43033',
    borderRadius: 10, padding: '12px 16px',
    display: 'flex', alignItems: 'center', gap: 10,
    marginBottom: 24,
  },

  statCard: {
    background: '#111128', borderRadius: 14, border: '1px solid #1e1e3a',
    padding: '20px 28px', marginBottom: 24, display: 'inline-block', minWidth: 180,
  },

  tableCard: {
    background: '#111128', borderRadius: 16,
    border: '1px solid #1e1e3a', overflowX: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    color: '#666', fontSize: 12, fontWeight: '600',
    padding: '14px 16px', textAlign: 'left',
    borderBottom: '1px solid #1e1e3a', whiteSpace: 'nowrap',
  },
  td: {
    color: '#ccc', fontSize: 13,
    padding: '14px 16px', borderBottom: '1px solid #1e1e3a11',
    verticalAlign: 'middle',
  },

  passwordBadge: {
    background: '#00c89622', color: '#00c896',
    padding: '4px 10px', borderRadius: 6,
    fontSize: 13, fontWeight: 'bold',
    fontFamily: 'monospace', letterSpacing: 1,
  },
  pendingBadge: {
    background: '#f4c43022', color: '#f4c430',
    padding: '3px 10px', borderRadius: 6,
    fontSize: 12, fontWeight: 'bold',
  },

  changeBtn: {
    background: '#7c3aed22', border: '1px solid #7c3aed44',
    color: '#a78bfa', borderRadius: 8, padding: '7px 14px',
    cursor: 'pointer', fontSize: 12, fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  doneBtn: {
    background: '#ff4a4a11', border: '1px solid #ff4a4a33',
    color: '#ff6b6b', borderRadius: 8, padding: '7px 14px',
    cursor: 'pointer', fontSize: 12, fontWeight: '600',
  },
};